import Button from 'plaid-threads/Button';
import axios from 'axios';
import TextInput from 'plaid-threads/TextInput';
import NumberInput from 'plaid-threads/NumberInput';
import Select from 'plaid-threads/Select';
import InputGroup from 'plaid-threads/InputGroup';
import React, { useMemo } from 'react';
import { Column, Grid, SelectGroup, ToggleSwitch } from 'plaid-threads';
import AlertError from 'plaid-threads/Icons/AlertError';
import { AppContext } from '../App';
import {
  PRODUCT_EXPERIENCE_OPTIONS,
  PAYMENT_SCHEME_OPTIONS,
  PaymentSchemeSelectOptionType,
  PaymentSchemeType,
  Currency,
} from '../constants';
import './index.scss';
import { getAllCurrenciesForCountry, getAmountString } from '../currencyUtils';
import { MultiCountryCodeSelector } from '../Header';

function findPaymentSchemeValue(
  scheme: PaymentSchemeType | null,
): PaymentSchemeSelectOptionType | null {
  if (!scheme) {
    return null;
  }

  const options = [...PAYMENT_SCHEME_OPTIONS];

  while (options.length) {
    const option = options.pop();

    if (option?.value === scheme) {
      return option;
    }

    if (option?.options && Array.isArray(option.options)) {
      options.push(...option.options);
    }
  }

  return null;
}

const ALL_ACCOUNT_PRODUCTS = [
  'auth',
  'transactions',
  'identity',
  'investments',
];

const ACCOUNT_PRODUCT_LABELS: { [key: string]: string } = {
  investments: 'Investments (Alpha)',
};

const PFC_VERSION_OPTIONS = [
  { label: 'v2', value: 'v2' },
  { label: 'v1', value: 'v1' },
];

interface Props {}

const Landing: React.FC<Props> = (props) => {
  const {
    dispatch,
    amount,
    countryCode,
    currency,
    scheme,
    productExperience,
    accountProducts,
    pfcVersion,
  } = React.useContext(AppContext);

  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [authenticationError, setAuthenticationError] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [accountProductsMap, setAccountProductsMap] = React.useState<{
    [key: string]: boolean;
  }>(
    Object.fromEntries(
      ALL_ACCOUNT_PRODUCTS.map((productKey) => [
        productKey,
        accountProducts.indexOf(productKey) >= 0,
      ]),
    ),
  );

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const key = params.get('key');
  const timestamp = params.get('timestamp');
  const allCurrenciesSupported = getAllCurrenciesForCountry(countryCode);
  const hasMultipleCurrencies = allCurrenciesSupported.length > 1;

  const updateAccountProducts = (key: string) => {
    const oldValue = accountProductsMap[key];
    setAccountProductsMap({
      ...accountProductsMap,
      [key]: !oldValue,
    });
  };

  React.useEffect(() => {
    dispatch({
      type: 'SET_AMOUNT',
      payload: {
        amount: 0,
      },
    });
    dispatch({
      type: 'SET_PRODUCT_EXPERIENCE',
      payload: {
        productExperience: '',
      },
    });
  }, [dispatch]);

  const emailComponent = (
    <div>
      <div className="Landing__instructions">Please enter your email</div>
      <TextInput
        id="email"
        label="Email address"
        errorMessage={authenticationError}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
      />
    </div>
  );

  const nameComponent = (
    <div>
      <div className="Landing__instructions">Please enter your full name</div>
      <TextInput
        id="name"
        label="Full name"
        onChange={(e) => setName(e.target.value)}
        placeholder="Full name"
      />
    </div>
  );

  const pisConfigurationComponent = (
    <div>
      <div className="Landing__instructions">
        Enter the payment amount (max of {getAmountString(5, currency)})
      </div>
      <Grid fullWidth className="Landing__amount">
        <Column medium="auto" small={12}>
          <NumberInput
            id="amount"
            label="Payment amount"
            errorMessage={amountError}
            onChange={(e) =>
              dispatch({
                type: 'SET_AMOUNT',
                payload: {
                  amount: parseFloat(e.target.value),
                },
              })
            }
            placeholder="Payment amount"
          />
        </Column>
        {hasMultipleCurrencies && (
          <Column medium={3} small={12}>
            <Select
              menuPlacement="top"
              label="Currency"
              id="currency"
              placeholder="Select currency"
              options={allCurrenciesSupported.map(getSelectOptionsForCurrency)}
              value={getSelectOptionsForCurrency(currency)}
              onChange={(option: CurrencySelectOption) =>
                dispatch({
                  type: 'SET_CURRENCY',
                  payload: {
                    currency: option.value,
                  },
                })
              }
            />
          </Column>
        )}
      </Grid>
      <InputGroup
        legend="Payment scheme"
        className="Landing__instructions__scheme"
      >
        <Select
          menuPlacement="top"
          isClearable
          label="Scheme"
          id="scheme"
          placeholder="Select payment scheme"
          options={PAYMENT_SCHEME_OPTIONS}
          value={findPaymentSchemeValue(scheme)}
          onChange={(option: PaymentSchemeSelectOptionType | null) =>
            dispatch({
              type: 'SET_SCHEME',
              payload: {
                scheme: option?.value || null,
              },
            })
          }
        />
      </InputGroup>
    </div>
  );

  const aisConfigurationComponent = (
    <div>
      <div className="Landing__instructions">
        Please select Account Products (at least one)
      </div>
      <Grid fullWidth>
        {ALL_ACCOUNT_PRODUCTS.map((productKey) => (
          <ToggleSwitch
            key={productKey}
            id={productKey}
            value={accountProductsMap[productKey]}
            onChange={() => updateAccountProducts(productKey)}
          >
            <span style={{ textTransform: 'capitalize' }}>
              {ACCOUNT_PRODUCT_LABELS[productKey] ?? productKey}
            </span>
          </ToggleSwitch>
        ))}
      </Grid>
      {accountProductsMap['transactions'] && (
        <div style={{ marginTop: '16px' }}>
          <Select
            id="pfcVersion"
            label="PFC Version"
            options={PFC_VERSION_OPTIONS}
            value={PFC_VERSION_OPTIONS.find((opt) => opt.value === pfcVersion)}
            onChange={(option: { label: string; value: string }) =>
              dispatch({
                type: 'SET_PFC_VERSION',
                payload: { pfcVersion: option.value },
              })
            }
          />
        </div>
      )}
    </div>
  );

  const pisDonateConfigurationComponent = (
    <div>
      <div className="Landing__instructions">
        Enter the payment amount (max of {getAmountString(5, currency)})
      </div>
      <NumberInput
        id="amount"
        label="Payment amount"
        errorMessage={amountError}
        onChange={(e) =>
          dispatch({
            type: 'SET_AMOUNT',
            payload: {
              amount: parseFloat(e.target.value),
            },
          })
        }
        placeholder="Payment amount"
      />
      {amountError && (
        <div className="Landing__error">
          <span className="Landing__errorIcon">
            <AlertError />
          </span>
          {amountError}
        </div>
      )}
    </div>
  );

  const productExperienceComponent = (
    <div style={{ display: countryCode.length < 1 ? 'none' : 'block' }}>
      <div className="Landing__instructions">
        Select your preferred product experience
      </div>
      <SelectGroup
        value={{ label: productExperience || '', value: productExperience }}
        options={
          countryCode.length === 1
            ? PRODUCT_EXPERIENCE_OPTIONS[countryCode[0]]
            : PRODUCT_EXPERIENCE_OPTIONS['DE']
        }
        multi={false}
        onChange={(opt) =>
          dispatch({
            type: 'SET_PRODUCT_EXPERIENCE',
            payload: {
              productExperience: opt.value,
            },
          })
        }
        className="Landing__productExperiences"
        buttonClassName="Landing__productExperiences__button"
      ></SelectGroup>
      {productExperience === 'PIS' && pisConfigurationComponent}
      {productExperience === 'Donate to Unicef' &&
        pisDonateConfigurationComponent}
      {productExperience === 'identitymatch' && nameComponent}
      {productExperience === 'AIS' && aisConfigurationComponent}
    </div>
  );

  const accountProductList = useMemo(
    () =>
      Object.entries(accountProductsMap)
        .filter(([_productKey, isProductEnabled]) => isProductEnabled)
        .map(([productKey]) => productKey),
    [accountProductsMap],
  );

  const onSubmit = async (evt: any) => {
    // Prevent browser from submitting the form.
    evt.preventDefault();
    evt.stopPropagation();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/login`,
        { email: email, key: key, timestamp: timestamp },
        { withCredentials: true },
      );
      if (response.status === 200) {
        switch (productExperience) {
          case 'AIS':
            dispatch({
              type: 'SET_ACCOUNT_PRODUCTS',
              payload: {
                accountProducts: accountProductList,
              },
            });
            dispatch({
              type: 'SET_PAGE',
              payload: {
                page: 'ais',
              },
            });
            break;
          case 'PIS':
            // validate amount
            if (!amount || amount < 1 || amount > 5) {
              setAmountError('Please enter an amount between 1 and 5');
            } else {
              dispatch({
                type: 'SET_PAGE',
                payload: {
                  page: 'pis',
                },
              });
            }
            break;
          case 'Donate to Unicef':
            dispatch({
              type: 'SET_SCHEME',
              payload: {
                scheme: 'LOCAL_DEFAULT',
              },
            });
            dispatch({
              type: 'SET_IS_DONATION',
              payload: {
                isDonation: true,
              },
            });
            // validate amount
            if (!amount || amount < 1 || amount > 5) {
              setAmountError('Please enter an amount between 1 and 5');
            } else {
              dispatch({
                type: 'SET_PAGE',
                payload: {
                  page: 'pis',
                },
              });
            }
            break;
          case 'consents':
            dispatch({
              type: 'SET_PAGE',
              payload: {
                page: 'consents',
              },
            });
            break;
          case 'updatemode':
            dispatch({
              type: 'SET_PAGE',
              payload: {
                page: 'updatemode',
              },
            });
            break;
          case 'identitymatch':
            dispatch({
              type: 'SET_PAGE',
              payload: {
                page: 'ais',
              },
            });
            dispatch({
              type: 'SET_NAME',
              payload: {
                userFullName: name,
              },
            });
            break;
        }
      }
    } catch (error) {
      setAuthenticationError('Authentication failed. Please try again.');
    }
  };

  return (
    <div className="Landing">
      <div className="Landing__title">Product demo</div>
      {emailComponent}
      <div>
        <div className="Landing__instructions">Please Select Countries</div>
        <MultiCountryCodeSelector />
      </div>
      {productExperienceComponent}
      <div className="Landing__button">
        <Button
          wide
          onClick={onSubmit}
          disabled={
            countryCode.length < 1 ||
            !email ||
            !productExperience ||
            (productExperience === 'PIS' && !amount) ||
            (productExperience === 'identitymatch' && !name) ||
            (productExperience === 'AIS' && accountProductList.length <= 0)
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default Landing;

type CurrencySelectOption = { label: Currency; value: Currency };
function getSelectOptionsForCurrency(currency: Currency): CurrencySelectOption {
  return { label: currency, value: currency };
}
